import { formatChipsShort, formatNOK } from '@polf/shared/locale'

import type { PokerPlayerSeatFragment } from '../../../__generated__/hooks'
import type { PayJump } from '../../../util/poker'

/**
 * Pay-jump line copy. Three flavours:
 *  - bubble (>2): pure count — "{n} til premiepenger"
 *  - bubble (≤2): handled by BubbleBanner instead; we return null here.
 *  - secured ITM:
 *      - With a rung above: "{position}. plass sikret · {kr} kr (neste {next} kr)"
 *      - Final table (no rung): "{position}. plass sikret · {kr} kr"
 */
const PayJumpLine = ({ payJump }: { payJump: PayJump }) => {
  if (payJump.kind === 'bubble') {
    if (payJump.toGo <= 2) return null
    return (
      <span className="text-ink-500">
        <span className="font-mono num font-bold text-ink-900">
          {payJump.toGo}
        </span>{' '}
        til premiepenger
      </span>
    )
  }

  const { positionIfNow, securedKr, nextPositionKr } = payJump
  return (
    <span className="text-ink-500">
      <span className="font-mono num font-bold text-ink-900">
        {positionIfNow}. plass
      </span>{' '}
      sikret ·{' '}
      <span className="font-mono num font-bold text-gold">
        {formatNOK(securedKr)}
      </span>
      {nextPositionKr != null ? (
        <span className="block text-[10px] mt-0.5">
          Neste: {formatNOK(nextPositionKr)}
        </span>
      ) : null}
    </span>
  )
}

/**
 * The seat hero — 100% about ME. Bord / Sete / my live currentStack /
 * my BBs / my pay-jump.
 *
 * Field stats (players-left, avg stack) deliberately do NOT live here —
 * they're tournament-wide and belong on `Clock`. We keep ONE cross-
 * reference: a tiny "vs snitt {X} BB" sub-line under my BB number, so
 * the personal/global comparison is one glance away without ceding the
 * card to global state.
 *
 * BB ratio is computed against the LIVE stack — short-stack players need
 * the number most, and "starting stack" is irrelevant after the first hand.
 */
const SeatCard = ({
  seat,
  myBB,
  avgBB,
  payJump,
  allowEdit,
  onEditStack,
}: {
  seat: PokerPlayerSeatFragment
  myBB: number | null
  avgBB: number | null
  payJump: PayJump | null
  allowEdit: boolean
  onEditStack: () => void
}) => (
  <div className="mx-2 my-3 bg-paper rounded-lg p-4 shadow flex flex-col gap-3">
    <div className="flex items-start gap-6">
      <div>
        <div className="text-[10px] up text-ink-500">Bord</div>
        <div className="font-display font-extrabold text-3xl leading-none text-green-900">
          {seat.tableNumber}
        </div>
      </div>
      <div>
        <div className="text-[10px] up text-ink-500">Sete</div>
        <div className="font-display font-extrabold text-3xl leading-none text-green-900">
          {seat.seatNumber}
        </div>
      </div>
      {/* Whole stack column is one tap target when editable. Previously
          only the number itself was clickable (~40px), which required
          precise tapping on mobile — the "Endre" badge above the number
          isn't clickable in itself, and users kept missing. */}
      {allowEdit ? (
        <button
          type="button"
          onClick={onEditStack}
          aria-label="Endre chipstack"
          className="flex-1 text-right min-h-[64px] -mr-2 pr-2 rounded-lg active:bg-ink-100/60 transition-colors"
        >
          <div className="text-[10px] up text-ink-500 flex items-center justify-end gap-1.5">
            <span>Din stack</span>
            <span className="text-[9px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full normal-case tracking-normal">
              Endre
            </span>
          </div>
          <div className="font-mono num font-bold text-2xl text-ink-900 underline decoration-dashed decoration-ink-300 underline-offset-4">
            {formatChipsShort(seat.currentStack)}
          </div>
          {myBB !== null ? (
            <div className="text-[11px] text-ink-500 num">
              {myBB} BB
              {avgBB !== null && avgBB > 0 ? (
                <span className="text-ink-400"> · snitt {avgBB} BB</span>
              ) : null}
            </div>
          ) : null}
        </button>
      ) : (
        <div className="flex-1 text-right">
          <div className="text-[10px] up text-ink-500">Din stack</div>
          <div className="font-mono num font-bold text-2xl text-ink-900">
            {formatChipsShort(seat.currentStack)}
          </div>
          {myBB !== null ? (
            <div className="text-[11px] text-ink-500 num">
              {myBB} BB
              {avgBB !== null && avgBB > 0 ? (
                <span className="text-ink-400"> · snitt {avgBB} BB</span>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
    {/* Pay-jump line — own row so the layout reads top-to-bottom:
        stack → snitt/igjen → premiepenger. */}
    {payJump ? (
      <div className="text-xs border-t border-ink-100 pt-3">
        <PayJumpLine payJump={payJump} />
      </div>
    ) : null}
  </div>
)

export default SeatCard
